// NowPlaying.js
import React, { useContext, useState } from "react";
import { NowPlayingContext } from "./NowPlayingProvider";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import Modal from "@mui/material/Modal";
import Button from "@mui/material/Button";
import { IconButton } from '@mui/material';
import CircularProgress from "@mui/material/CircularProgress";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import LooksOneSharpIcon from "@mui/icons-material/LooksOneSharp";
import LooksTwoSharpIcon from "@mui/icons-material/LooksTwoSharp";
import { PiSoundcloudLogoDuotone } from "react-icons/pi";
import FlashingLiveIndicator from "./FlashingLiveIndicator";
import BroadcastIndicator from "./BroadcastIndicator";
import useFadeOnChange from "./useFadeOnChange";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
};

const NowPlaying = () => {
  const {
    liveStreamData,
    station,
    isLoading,
    isLive,
    isPlaying,
    setIsPlaying,
    isLiveStream,
    setIsLiveStream,
    setStreamUrl,
    setLiveStreamNowPlayingData,
    setCurrentTrackId,
  } = useContext(NowPlayingContext);
  const np = liveStreamData; // Always use live stream data

  const [open, setOpen] = useState(false);
  const [modalImage, setModalImage] = useState("");

  const liveStreamDataKey = JSON.stringify(liveStreamData);
  const artFade = useFadeOnChange(liveStreamDataKey, 1200);
  const textFade = useFadeOnChange(liveStreamDataKey, 900);

  const handleOpen = (image) => {
    setOpen(true);
    setModalImage(image);
  };

  const handleClose = () => setOpen(false);

  const title = np?.title || "No title available";
  const artist =
    np?.title === "Live Broadcast" ? "" : np?.artist || "";
  const art = np?.art || "https://placehold.co/600x400";

  const buttonColor = station === "wssr" ? "primary" : "secondary";
  const deckButton = isLoading ? (
    <Box display="flex" alignItems="center" sx={{ cursor: "none" }}>
      <CircularProgress size={16} color={buttonColor} sx={{ marginLeft: 1 }} />
    </Box>
  ) : (
    <Button
      size="small"
      color={buttonColor}
      endIcon={station === "wssr" ? <LooksOneSharpIcon /> : <LooksTwoSharpIcon />}
      sx={{ fontSize: '0.7rem', padding: '2px 8px', minHeight: 0 }}
    >
      Deck
    </Button>
  );

  const heroIsPlaying = isPlaying && isLiveStream;

  const handlePlayToggle = (event) => {
    event.stopPropagation();
    if (heroIsPlaying) {
      setIsPlaying(false);
      return;
    }

    setIsPlaying(false);
    setCurrentTrackId(null);
    setIsLiveStream(true);
    setStreamUrl(
      station === "wssr"
        ? "https://broadcast.wssr.stream/listen/wssr/radio.mp3"
        : "https://broadcast.wssr.stream/listen/wssr_node_2/radio.mp3"
    );
    setLiveStreamNowPlayingData();
    setIsPlaying(true);
  };

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: { md: 420 },
        overflow: "hidden",
      }}
    >
      {isLoading ? (
        <Skeleton
          variant="rectangular"
          sx={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
        />
      ) : (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${art})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "blur(30px) brightness(0.7)",
            transform: "scale(1.15)",
            opacity: artFade.opacity,
            transition: artFade.transition,
          }}
        />
      )}

      {/* Dark gradient overlay so overlaid text/controls stay readable */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: "linear-gradient(to bottom, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.55) 60%, rgba(0,0,0,0.9) 100%)",
          pointerEvents: "none",
        }}
      />

      {np?.custom_fields?.elink && (
        <IconButton
          component="a"
          href={np?.custom_fields?.elink}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(event) => event.stopPropagation()}
          size="small"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            padding: '6px',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: '#FF991C',
            borderRadius: '0%',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
            },
          }}
        >
          <PiSoundcloudLogoDuotone />
        </IconButton>
      )}

      {/* Overlaid now-playing content */}
      <Box
        sx={{
          position: { xs: "relative", md: "absolute" },
          left: { md: 0 },
          right: { md: 0 },
          bottom: { md: 0 },
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "center",
          gap: { xs: 1.5, md: 2 },
          padding: 3,
          textAlign: { xs: "center", md: "left" },
        }}
      >
        {isLoading ? (
          <Skeleton
            variant="rectangular"
            sx={{ flexShrink: 0, width: { xs: 200, md: 180 }, height: { xs: 200, md: 180 }, borderRadius: 1 }}
          />
        ) : (
          <Box
            onClick={() => handleOpen(art)}
            sx={{
              flexShrink: 0,
              width: { xs: 200, md: 180 },
              height: { xs: 200, md: 180 },
              borderRadius: 1,
              overflow: "hidden",
              cursor: "pointer",
              boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
              opacity: artFade.opacity,
              transition: artFade.transition,
            }}
          >
            <img
              src={art}
              alt={title}
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
          </Box>
        )}

        <Box sx={{ minWidth: 0, alignSelf: { xs: "stretch", md: "auto" }, flex: { md: 1 } }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent={{ xs: "center", md: "flex-start" }}
            gap={1}
            sx={{ marginBottom: 1 }}
          >
            {deckButton}
            {isLive ? (
              <FlashingLiveIndicator station={station} />
            ) : (
              <BroadcastIndicator station={station} />
            )}
          </Box>

          {isLoading ? (
            <>
              <Skeleton variant="text" width={220} height={36} sx={{ margin: { xs: "0 auto", md: 0 } }} />
              <Skeleton variant="text" width={140} height={26} sx={{ margin: { xs: "0 auto", md: 0 } }} />
            </>
          ) : (
            <Box sx={{ opacity: textFade.opacity, transition: textFade.transition }}>
              <Typography
                variant="h5"
                sx={{ fontWeight: 700, textOverflow: "ellipsis", overflow: 'hidden', whiteSpace: 'nowrap' }}
              >
                {title}
              </Typography>
              <Typography variant="subtitle1" sx={{ color: "#ccc", textOverflow: "ellipsis", overflow: 'hidden', whiteSpace: 'nowrap' }}>
                {artist}
              </Typography>
            </Box>
          )}
        </Box>

        <IconButton
          onClick={handlePlayToggle}
          disabled={isLoading}
          sx={{
            flexShrink: 0,
            width: { xs: 52, md: 64 },
            height: { xs: 52, md: 64 },
            backgroundColor: 'primary.main',
            color: '#000',
            '&:hover': {
              backgroundColor: 'primary.main',
              transform: 'scale(1.05)',
            },
          }}
        >
          {heroIsPlaying ? <PauseIcon fontSize="large" /> : <PlayArrowIcon fontSize="large" />}
        </IconButton>
      </Box>

      {/* Modal for Enlarged Album Art */}
      <Modal
        open={open}
        onClose={handleClose}
        disableAutoFocus={true}
        aria-labelledby="modal-image"
        aria-describedby="modal-image-description"
      >
        <Box sx={style}>
          <img src={modalImage} alt="Expanded Art" style={{ maxWidth: "90vw", maxHeight: "90vh" }} />
        </Box>
      </Modal>
    </Box>
  );
};

export default NowPlaying;
